/**
 * React Query hook for user feature flags
 * Provides access to user-level feature toggles
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import {
  getUserPreferences,
  updateUserFlag,
  setActiveTeamPreference,
  UserFeatureFlags,
} from '@/services/userPreferences.service';
import { logger } from '@/lib/logger';

/**
 * Query keys factory for user preferences
 */
export const userPreferencesKeys = {
  all: (userId: string) => ['userPreferences', userId] as const,
  preferences: (userId: string) => [...userPreferencesKeys.all(userId), 'prefs'] as const,
};

/**
 * Hook to get all user preferences
 */
export function useUserPreferences() {
  const { user } = useAuth();
  const userId = user?.uid;

  return useQuery({
    queryKey: userPreferencesKeys.preferences(userId || ''),
    queryFn: async () => {
      if (!userId) {
        throw new Error('User not authenticated');
      }
      return getUserPreferences(userId);
    },
    enabled: !!userId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
  });
}

/**
 * Hook to check if a specific feature flag is enabled
 *
 * @example
 * const isNotepadEnabled = useUserFlag('notepad');
 */
export function useUserFlag(flag: keyof UserFeatureFlags): boolean {
  const { data: preferences } = useUserPreferences();
  return preferences?.flags[flag] ?? false;
}

/**
 * Hook to update a feature flag
 */
export function useUpdateUserFlag() {
  const { user } = useAuth();
  const userId = user?.uid;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ flag, value }: { flag: keyof UserFeatureFlags; value: boolean }) => {
      if (!userId) {
        throw new Error('User not authenticated');
      }
      return updateUserFlag(userId, flag, value);
    },
    onSuccess: () => {
      // Invalidate preferences cache
      void void queryClient.invalidateQueries({
        queryKey: userPreferencesKeys.preferences(userId || ''),
      });
    },
    onError: (error: Error) => {
      logger.error('[useUpdateUserFlag] error:', error);
    },
  });
}

/**
 * Convenience hook for the notepad feature flag
 *
 * @example
 * const isNotepadEnabled = useNotepadEnabled();
 */
export function useNotepadEnabled(): boolean {
  return useUserFlag('notepad');
}

/**
 * Hook to get the user's active team preference
 * INV-SEC-UI-CTX-1: This is UX-only, not a security anchor.
 *
 * @returns activeTeamId or null if not set
 */
export function useActiveTeamPreference(): string | null {
  const { data: preferences } = useUserPreferences();
  return preferences?.activeTeamId ?? null;
}

/**
 * Hook to set the user's active team preference
 * Uses merge semantics on /users/{uid}/private/preferences.
 * INV-SEC-UI-CTX-1: This is UX-only, not a security anchor.
 */
export function useSetActiveTeamPreference() {
  const { user } = useAuth();
  const userId = user?.uid;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (teamId: string | null) => {
      if (!userId) {
        throw new Error('User not authenticated');
      }
      return setActiveTeamPreference(userId, teamId);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: userPreferencesKeys.preferences(userId || ''),
      });
    },
    onError: (error: Error) => {
      logger.error('[useSetActiveTeamPreference] error:', error);
    },
  });
}
